import { ReactElement } from 'react';
import { format } from 'date-fns';

import { useGetMainTokenUSDRatioQuery } from 'modules/api/actions/getMainTokenUSDRatio';
import { mapDataToUndefinedIfSkip } from 'modules/api/utils';
import { Table } from 'modules/common/components/Table';
import { DEFAULT_LONG_DECIMAL_PLACES } from 'modules/common/const';
import { AmountCell } from 'modules/delegateStaking/components/AmountCell';
import { PoolCell } from 'modules/delegateStaking/components/PoolCell';
import { UnlockClaimCell } from 'modules/delegateStaking/components/UnlockClaimCell/UnlockClaimCell';
import { useGlobalTranslation } from 'modules/i18n/hooks/useGlobalTranslation';
import { getPoolEndpoint } from 'modules/pool/actions/getPool';
import { useGetPoolMetaQuery } from 'modules/pool/actions/getPoolMeta';
import { IPoolUnstake } from 'modules/pool/types';

import { translation } from '../ClaimTable/translation';
import { useStyles } from './useStyles';

interface IDelegateTableProps {
  className?: string;
  isHidePoolName?: boolean;
  isLSClaim?: boolean;
  poolUnstake: IPoolUnstake;
}

export function ClaimRow({
  className,
  poolUnstake,
  isLSClaim,
  isHidePoolName,
}: IDelegateTableProps): ReactElement | null {
  const { classes } = useStyles();
  const { t, keys } = useGlobalTranslation(translation);

  const { amount, executedAt, poolAddress } = poolUnstake;
  const { data: pool } = getPoolEndpoint.useQueryState(
    {
      address: poolAddress,
    },
    {
      skip: isHidePoolName,
      selectFromResult: mapDataToUndefinedIfSkip,
    },
  );

  const { data: poolMeta } = useGetPoolMetaQuery(
    {
      address: poolAddress,
    },
    {
      skip: isHidePoolName,
      selectFromResult: mapDataToUndefinedIfSkip,
    },
  );

  const { data: usdRatio } = useGetMainTokenUSDRatioQuery();

  if (!pool && !isHidePoolName) {
    return null;
  }

  return (
    <Table.Row className={className}>
      {!isHidePoolName && (
        <PoolCell
          address={poolUnstake.poolAddress}
          className={classes.poolCell}
          poolLogoIpfsLink={poolMeta?.ipfsImageLink}
          poolName={poolMeta?.name}
        />
      )}

      <AmountCell
        amount={t(keys.unit.tokenValue, {
          value: amount.decimalPlaces(DEFAULT_LONG_DECIMAL_PLACES).toFormat(),
          token: t(keys.tokens.main),
        })}
        className={classes.commonMdCell}
        mdLabel={t(keys.withdrawalAmount)}
        subAmount={
          usdRatio && !usdRatio.isZero() && amount
            ? t(keys.unit.$, {
                value: amount
                  .decimalPlaces(DEFAULT_LONG_DECIMAL_PLACES)
                  .toFormat(),
                token: t(keys.tokens.main),
              })
            : undefined
        }
      />

      <AmountCell
        amount={format(executedAt, 'MMM dd, yyyy')}
        className={classes.commonMdCell}
        mdLabel={t(keys.initiated)}
        subAmount={format(executedAt, 'hh:mm a')}
      />

      <UnlockClaimCell
        align="right"
        className={classes.actions}
        executedAt={executedAt}
        isLSClaim={isLSClaim}
        poolAddress={poolAddress}
      />
    </Table.Row>
  );
}
